import { useTranslation } from "react-i18next"
import { FileText, Info } from "lucide-react"
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@f2e/ui/components/base"
import { CopilotHiddenField } from "@/components/Copilot/CopilotHiddenField"
import UploadDropzone from "@/components/UploadDropzone"

// Type for CSV row data parsed from evaluation case CSV files
export interface CsvRowData {
  input: string
  expected_output: string
  note?: string
  rubric?: string
  [key: string]: string | undefined // Allow for additional columns
}

interface CsvPreviewModalProps {
  isOpen: boolean
  rows: CsvRowData[]
  errors: string[]
  file: File | null
  onCancel: () => void
  onConfirm: () => void
  onFileSelected: (file: File) => void
  uploading: boolean
  uploadDisabled?: boolean
}

const CsvPreviewModal = ({
  isOpen,
  rows,
  errors,
  file,
  onCancel,
  onConfirm,
  onFileSelected,
  uploading,
  uploadDisabled = false,
}: CsvPreviewModalProps) => {
  const { t } = useTranslation()

  const headers =
    rows.length > 0
      ? Object.keys(rows[0])
      : ["input", "expected_output", "note", "rubric"]

  const handleFilesSelected = (files: File[]) => {
    if (files[0]) onFileSelected(files[0])
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onCancel()}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            {t("evaluation.evaluationTable.csvPreview.title")}
          </DialogTitle>
          <DialogDescription>
            {t("evaluation.evaluationTable.csvPreview.description")}
          </DialogDescription>
        </DialogHeader>
        <CopilotHiddenField
          fieldKey="csvPreview.summary"
          value={`rows=${rows.length}; errors=${errors.length}; uploading=${uploading ? "true" : "false"}; disabled=${uploadDisabled ? "true" : "false"}; file=${file ? "selected" : "none"}`}
        />

        <div className="space-y-4 flex-1 min-h-0 overflow-auto">
          <div className="rounded-lg border bg-muted/30 p-4 space-y-3">
            <div className="flex items-start gap-2">
              <Info className="h-4 w-4 text-muted-foreground mt-0.5" />
              <div className="flex-1 space-y-2 text-sm">
                <div>
                  <span className="font-medium">
                    {t("evaluation.evaluationTable.csvPreview.requiredFields")}
                  </span>
                  <span className="text-muted-foreground">
                    input, expected_output
                  </span>
                </div>
                <div>
                  <span className="font-medium">
                    {t("evaluation.evaluationTable.csvPreview.optionalFields")}
                  </span>
                  <span className="text-muted-foreground">note, rubric</span>
                </div>
                <div className="text-xs text-muted-foreground">
                  {t("evaluation.evaluationTable.csvPreview.rubricHint")}
                </div>
              </div>
            </div>
            <div className="rounded-md bg-background border p-3 font-mono text-xs">
              <div className="text-muted-foreground mb-1">
                {t("evaluation.evaluationTable.csvPreview.example")}
              </div>
              <div className="space-y-1">
                <div className="text-foreground">
                  input,expected_output,note,rubric
                </div>
                <div className="text-muted-foreground">
                  How tall is Everest?,8849 m,geography,Must include the unit
                </div>
              </div>
            </div>
          </div>

          <UploadDropzone
            file={file}
            onFilesSelected={handleFilesSelected}
            selectFileLabel={t(
              "evaluation.evaluationTable.csvPreview.selectFile"
            )}
            supportedFormatLabel={t(
              "evaluation.evaluationTable.csvPreview.supportedFormat"
            )}
            chooseFileLabel={t(
              "evaluation.evaluationTable.csvPreview.chooseFile"
            )}
            rechooseFileLabel={t(
              "evaluation.evaluationTable.csvPreview.rechooseFile"
            )}
          />

          {errors.length > 0 && (
            <div className="text-red-500 dark:text-red-300 text-sm space-y-1">
              {errors.map((err, i) => (
                <div key={i}>{err}</div>
              ))}
            </div>
          )}

          {file && (
            <div className="overflow-auto border rounded">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">#</TableHead>
                    {headers.map((h) => (
                      <TableHead key={h} className="whitespace-nowrap">
                        {h}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.slice(0, 10).map((row, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="text-gray-500">{idx + 1}</TableCell>
                      {headers.map((h) => (
                        <TableCell key={h} className="whitespace-nowrap">
                          {row[h]}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                  {rows.length === 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={headers.length + 1}
                        className="text-center py-4 text-gray-400"
                      >
                        {t("evaluation.evaluationTable.csvPreview.noData")}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}

          {file && rows.length > 10 && (
            <div className="text-xs text-gray-400">
              {t("evaluation.evaluationTable.csvPreview.showingRows", {
                count: rows.length,
              })}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={uploading}
          >
            {t("evaluation.evaluationTable.csvPreview.cancel")}
          </Button>
          <Button
            type="button"
            onClick={onConfirm}
            disabled={!file || uploading || errors.length > 0 || uploadDisabled}
          >
            {uploading
              ? t("evaluation.evaluationTable.csvPreview.uploading")
              : t("evaluation.evaluationTable.csvPreview.upload")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default CsvPreviewModal
